import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { auth } from '../lib/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setFirebaseUser(firebaseUser);
        
        // For now, create a mock user data since Firestore isn't set up yet
        // In production, this would fetch from Firestore
        const mockUserData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || 'PFI Admin',
          role: 'admin', // Default to admin for first user
          phone: '+61 400 000 000',
          createdAt: new Date(),
          updatedAt: new Date(),
          preferences: {
            emailNotifications: true,
            smsNotifications: true
          }
        };
        
        setUser(mockUserData);
      } else {
        setFirebaseUser(null);
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email, password, userData) => {
    setLoading(true);
    try {
      const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update Firebase profile
      await updateProfile(firebaseUser, {
        displayName: userData.displayName
      });

      // In production, create user document in Firestore here

    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const sendEmailLink = async (email) => {
    // Use current domain for redirect
    const redirectUrl = `${window.location.origin}/auth/complete`;
    console.log('Sending email link with URL:', redirectUrl);
    console.log('Current origin:', window.location.origin);
    
    const actionCodeSettings = {
      // Use current domain - whatever is currently loading the app
      url: redirectUrl,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      console.log('✅ Email link sent successfully to:', email);
      console.log('📧 Check email (including spam/junk folder)');
      // Save email locally for completion
      window.localStorage.setItem('emailForSignIn', email);
    } catch (error) {
      console.error('Full error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  };

  const completeEmailLinkSignIn = async (email = null) => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      throw new Error('Invalid sign-in link');
    }

    // Get email from storage or parameter
    const userEmail = email || window.localStorage.getItem('emailForSignIn');
    if (!userEmail) {
      throw new Error('Email is required to complete sign-in');
    }

    const result = await signInWithEmailLink(auth, userEmail, window.location.href);
    // Clear email from storage
    window.localStorage.removeItem('emailForSignIn');
    return result;
  };

  const value = {
    user,
    firebaseUser,
    loading,
    signIn,
    signUp,
    logout,
    sendEmailLink,
    completeEmailLinkSignIn,
    isSignInWithEmailLink: () => isSignInWithEmailLink(auth, window.location.href)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};